import { useMemo, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Merge, ArrowRight, AlertTriangle } from "lucide-react";

const norm = (s) => String(s || "").toLowerCase().replace(/\s+/g, " ").trim();
const pickCanon = (items) => {
  const withArt = items.find((x) => String(x.art || "").trim());
  const withSpec = items.find((x) => x.spec);
  return (withArt || withSpec || items[0]).id;
};

export default function DuplicatenOpschonen({ open, onClose, rawMaterials, helpers, onMergeRaw, onMergeHelper, canEdit }) {
  const [kind, setKind] = useState("raw");
  const [bron, setBron] = useState("");
  const [doel, setDoel] = useState("");
  const [canonSel, setCanonSel] = useState({});

  const rawItems = (rawMaterials || []).map((material) => ({
    id: material.id,
    naam: material.naam,
    art: material.artikelnummer || "",
    leverancier: material.leverancier || "",
    spec: !!material.spec,
  }));
  const helpItems = (helpers || []).map((helper) => ({
    id: helper.id,
    naam: helper.naam,
    art: helper.artikelnr || "",
    leverancier: helper.leverancier || "",
    spec: !!helper.spec,
    type: helper.type || "Hulpstof",
  }));

  const groups = useMemo(() => {
    const build = (items, mergeFn, label, keyFor) => {
      const map = {};
      items.forEach((item) => {
        const key = keyFor(item);
        (map[key] = map[key] || []).push(item);
      });
      return Object.values(map).filter((g) => g.length > 1).map((g) => ({ items: g, mergeFn, label }));
    };
    return [
      ...build(
        rawItems,
        onMergeRaw,
        "Grondstof",
        (item) => [norm(item.naam), norm(item.leverancier), norm(item.art)].join("|"),
      ),
      ...build(
        helpItems,
        onMergeHelper,
        "Hulpstof",
        (item) => [
          norm(item.naam),
          norm(item.type),
          norm(item.leverancier),
          norm(item.art),
        ].join("|"),
      ),
    ];
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rawMaterials, helpers]);

  const canonFor = (g, idx) => canonSel[idx] ?? pickCanon(g.items);
  const list = kind === "raw" ? rawItems : helpItems;
  const doManual = () => {
    if (!bron || !doel || String(bron) === String(doel)) return;
    (kind === "raw" ? onMergeRaw : onMergeHelper)(doel, [bron]);
    setBron(""); setDoel("");
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="max-w-3xl max-h-[88vh] overflow-y-auto">
        <DialogHeader><DialogTitle className="flex items-center gap-2"><Merge className="h-5 w-5 text-[#1C64B0]" /> Dubbele artikelen opschonen</DialogTitle></DialogHeader>
        <p className="text-sm text-slate-500">Alleen échte dubbelen worden getoond: zelfde naam, leverancier, artikelnummer en (bij hulpstoffen) type. Artikelen met verschillende leveranciers blijven apart. Een hulpstof met dezelfde naam in twee types, zoals Breader én Predust, blijft bewust apart in de invoer maar deelt één voorraad op naam.</p>

        <div className="space-y-3">
          <div className="text-xs font-bold uppercase tracking-wide text-slate-500">Automatisch gevonden dubbelen ({groups.length})</div>
          {groups.length === 0 && <p className="text-sm text-slate-400">Geen artikelen met (bijna) dezelfde naam gevonden.</p>}
          {groups.map((g, idx) => { const canonId = canonFor(g, idx); return (
            <div key={idx} data-testid="dup-group" className="rounded-lg border border-slate-200 p-3">
              <div className="text-xs text-slate-400 mb-1.5">{g.label} · kies welk artikel je behoudt:</div>
              <div className="space-y-1">
                {g.items.map((x) => (
                  <label key={x.id} className="flex items-center gap-2 text-sm cursor-pointer">
                    <input type="radio" data-testid="dup-canon-radio" name={`canon-${idx}`} checked={String(canonId) === String(x.id)} onChange={() => setCanonSel((m) => ({ ...m, [idx]: x.id }))} />
                    <span className="font-medium text-slate-800">{x.naam}</span>
                    {x.art && <span className="font-mono text-[11px] text-slate-500">art {x.art}</span>}
                    {x.leverancier && <span className="text-[11px] text-slate-500">{x.leverancier}</span>}
                    {x.type && <span className="text-[11px] text-slate-500">{x.type}</span>}
                    {x.spec && <span className="rounded-full bg-emerald-100 text-emerald-700 px-1.5 text-[10px] font-semibold">spec</span>}
                  </label>
                ))}
              </div>
              <div className="mt-2 text-right">
                <Button size="sm" data-testid="dup-merge-group" disabled={!canEdit} className="bg-[#1C64B0] hover:bg-[#154E8A]" onClick={() => g.mergeFn(canonId, g.items.map((x) => x.id))}>
                  <Merge className="h-4 w-4 mr-1" /> Samenvoegen tot behouden artikel
                </Button>
              </div>
            </div>
          ); })}
        </div>

        <div className="rounded-lg border border-amber-200 bg-amber-50/50 p-3 space-y-2">
          <div className="text-xs font-bold uppercase tracking-wide text-amber-700 flex items-center gap-1"><AlertTriangle className="h-3.5 w-3.5" /> Handmatig samenvoegen (verschillende namen)</div>
          <div className="flex flex-wrap items-center gap-2">
            <select data-testid="dup-manual-kind" value={kind} onChange={(e) => { setKind(e.target.value); setBron(""); setDoel(""); }} className="h-9 rounded-md border border-slate-300 px-2 text-sm">
              <option value="raw">Grondstoffen</option>
              <option value="helper">Hulpstoffen</option>
            </select>
            <select data-testid="dup-manual-bron" value={bron} onChange={(e) => setBron(e.target.value)} className="h-9 rounded-md border border-slate-300 px-2 text-sm min-w-[200px]">
              <option value="">— verwijder (bron) —</option>
              {list.map((x) => <option key={x.id} value={x.id}>{x.naam}{x.art ? ` (${x.art})` : ""}</option>)}
            </select>
            <ArrowRight className="h-4 w-4 text-slate-400" />
            <select data-testid="dup-manual-doel" value={doel} onChange={(e) => setDoel(e.target.value)} className="h-9 rounded-md border border-slate-300 px-2 text-sm min-w-[200px]">
              <option value="">— behoud (doel) —</option>
              {list.map((x) => <option key={x.id} value={x.id}>{x.naam}{x.art ? ` (${x.art})` : ""}</option>)}
            </select>
            <Button size="sm" data-testid="dup-manual-merge" disabled={!canEdit || !bron || !doel || String(bron) === String(doel)} className="bg-[#1C64B0] hover:bg-[#154E8A]" onClick={doManual}>Samenvoegen</Button>
          </div>
          <p className="text-[11px] text-amber-700">Bijv. "Kuiken vellen 3 mm" (bron) → samenvoegen met bestaande "Vellen" (doel). De bron verdwijnt; alle koppelingen gaan naar het doel.</p>
        </div>

        <DialogFooter><Button variant="outline" onClick={onClose}>Sluiten</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
